<script setup lang="ts">
import { computed } from 'vue'
import { FairIconContainer } from '@/ui/primitives/FairIconContainer'
import type { FairIconName } from '@/ui/tokens/icons.data'
import {
  FAIR_ICON_BUTTON_METRICS,
  type FairIconButtonPreviewState,
  type FairIconButtonSize,
} from '@/ui/tokens/fair-icon-button.data'

/**
 * Icon button — RedisiginProductPage `1:966`
 * Medium / Small / Mobile · Current dot · pressed chip fill
 */
interface FairIconButtonProps {
  icon?: FairIconName | string
  size?: FairIconButtonSize
  /** Figma Current — 4px indicator under glyph */
  current?: boolean
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  ariaLabel?: string
  previewState?: FairIconButtonPreviewState
  interactive?: boolean
}

const props = withDefaults(defineProps<FairIconButtonProps>(), {
  icon: 'menu',
  size: 'medium',
  current: false,
  disabled: false,
  type: 'button',
  interactive: true,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const isDisabled = computed(
  () => props.disabled || props.previewState === 'disabled',
)

const metrics = computed(() => FAIR_ICON_BUTTON_METRICS[props.size])

const rootClass = computed(() => ({
  'fair-icon-button': true,
  [`fair-icon-button--size-${props.size}`]: true,
  'fair-icon-button--current': props.current,
  'fair-icon-button--disabled': isDisabled.value,
  'fair-icon-button--non-interactive': !props.interactive,
  [`fair-icon-button--preview-${props.previewState}`]: Boolean(
    props.previewState,
  ),
}))

function handleClick(event: MouseEvent) {
  if (!props.interactive || isDisabled.value) {
    return
  }
  emit('click', event)
}
</script>

<template>
  <button
    :type="type"
    :class="rootClass"
    :disabled="isDisabled"
    :aria-label="ariaLabel"
    :aria-current="current ? 'true' : undefined"
    @click="handleClick"
  >
    <span class="fair-icon-button__glyph">
      <slot>
        <FairIconContainer
          :name="icon"
          :size="metrics.icon"
          color="currentColor"
        />
      </slot>
    </span>
    <span
      v-if="current"
      class="fair-icon-button__indicator"
      aria-hidden="true"
    />
  </button>
</template>
